/**
 * FEATURE 4: New Markets TP (Markets_TP) - ISOLATED LOADER
 * Completely independent data loader for Feature 4 only
 * No conflicts with other features - has its own state and data sources
 */

import { useEffect, useRef } from "react";
import Papa from "papaparse";
import { fetchR2CsvText } from "../../utils/fetchR2Csv";

// DEBUG: Force log to verify file is loaded
console.log(`[FEATURE4_LOADER] FILE LOADED - ${new Date().toISOString()} - VERSION 2.0 - CACHE BUSTING TEST`);

// ISOLATED CACHE - Only for Feature 4
const feature4Cache = new Map();

// RegionID mapping cache for Feature 4
const regionIdMapping = new Map();

/**
 * Isolated Data Loader for Feature 4: New Markets TP
 * Handles Markets_TP turning point rankings independently
 */
export function useFeature4DataLoader({
  areaType,
  selectedName,
  setFeature4Data,
  setFeature4Loading,
  setFeature4Error,
  shouldLoad = true
}) {
  // DEBUG: Force log to verify hook is called
  console.log(`[FEATURE4_LOADER] HOOK CALLED - ${new Date().toISOString()} - VERSION 2.0 - CACHE BUSTING TEST`, {
    areaType,
    selectedName,
    shouldLoad
  });
  
  const hasLoadedRef = useRef(false);
  const hookId = useRef(Math.random().toString(36).substr(2, 9));
  const hasLoadedChartRef = useRef(false);
  const mappingLoadingRef = useRef(false); // Track if mapping is currently being loaded

  console.log(`[FEATURE4_LOADER] Hook called with areaType: ${areaType}, selectedName: ${selectedName}`);
  console.log(`[FEATURE4_LOADER] Current regionIdMapping keys:`, Array.from(regionIdMapping.keys()));
  console.log(`[FEATURE4_LOADER] shouldLoad value:`, shouldLoad);

  // Reset when area type changes
  useEffect(() => {
    console.log(`[FEATURE4_LOADER] Reset useEffect triggered - areaType: ${areaType}, shouldLoad: ${shouldLoad}, hasLoadedRef.current: ${hasLoadedRef.current}`);
    console.log(`[FEATURE4_LOADER] Reset useEffect dependencies:`, { areaType, shouldLoad });
    
    // Always reset the loaded flag when area type or shouldLoad changes
    hasLoadedRef.current = false;
    hasLoadedChartRef.current = false; // Reset chart loading flag too
    
    setFeature4Data({
      allDates: [],
      latestByMetro: {},
      bestPotential: [],
      tpByMetro: {},
      tpByRegionId: {},
      latestDate: "",
      chartData: []
    });
    setFeature4Loading(false);
    setFeature4Error(null);
    console.log(`[FEATURE4_LOADER] Reset completed for areaType: ${areaType} - hasLoadedRef reset to false`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaType, shouldLoad]);

  // Load data when area type is available
  useEffect(() => {
    console.log(`[FEATURE4_LOADER] useEffect START - shouldLoad: ${shouldLoad}, areaType: ${areaType}, hasLoaded: ${hasLoadedRef.current}`);
    console.log(`[FEATURE4_LOADER] useEffect triggered - shouldLoad: ${shouldLoad}, areaType: ${areaType}, hasLoaded: ${hasLoadedRef.current}`);
    console.log(`[FEATURE4_LOADER] Main useEffect dependencies:`, { areaType, shouldLoad });
    
    if (!shouldLoad) {
      console.log(`[FEATURE4_LOADER] Early return: shouldLoad is false`);
      return;
    }
    
    if (!areaType) {
      console.log(`[FEATURE4_LOADER] Early return: areaType is empty`);
      return;
    }
    
    // Check if we've already loaded data for this area type
    if (hasLoadedRef.current) {
      console.log(`[FEATURE4_LOADER] Early return: hasLoadedRef.current is true - data already loaded for ${areaType}`);
      return;
    }

    console.log(`[FEATURE4_LOADER] Starting data load for ${areaType} - setting hasLoadedRef to true`);
    hasLoadedRef.current = true;
    setFeature4Loading(true);
    setFeature4Error(null);

    const loadFeature4Data = async () => {
      try {
        const levelDir = areaType.toLowerCase();
        
        // FEATURE 4 DATA SOURCES - Markets_TP files
        const dataSources = {
          // Markets TP turning point data
          potential: `/data/Markets_TP/TP_Ready/${levelDir}_markets_TP_turning_points_ready.csv`,
          // Latest data for snapshots
          latest: `/data/Merged_Last_Month_Data/${levelDir}_5y_50k_merged.csv`,
          // Full data for charts
          full: `/data/Merged_Full_Data/${levelDir}_5y_50k_merged.csv`
        };

        // Load Markets TP data first
        const potentialUrl = `${dataSources.potential}?v=${Date.now()}`;
        console.log(`[FEATURE4_LOADER] Loading Markets TP data (R2-first) from: ${potentialUrl}`);

        const potentialText = await (async () => {
          const r2Key = `public/data/Markets_TP/TP_Ready/${levelDir}_markets_TP_turning_points_ready.csv`;
          console.log(`[FEATURE4_LOADER] Attempt R2 TP_Ready key: ${r2Key}`);
          return await fetchR2CsvText(r2Key);
        })();
        
        // Parse Markets TP data
        Papa.parse(potentialText, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          complete: async (result) => {
            try {
              const rows = result.data || [];
              console.log(`[FEATURE4_LOADER] Parsed ${rows.length} Markets TP rows`);
              console.log(`[FEATURE4_LOADER] Sample Markets TP data:`, rows.slice(0, 2));

              // Process Markets TP data
              const processedData = processMarketsTPData(rows, areaType);
              console.log(`[FEATURE4_LOADER] Processed Markets TP data:`, {
                bestPotentialCount: processedData.bestPotential.length,
                tpByMetroCount: Object.keys(processedData.tpByMetro).length,
                tpByRegionIdCount: Object.keys(processedData.tpByRegionId || {}).length,
                sampleBestPotential: processedData.bestPotential.slice(0, 2)
              });
              
              // Load latest data for snapshots AND Markets_TP Long Format for full date range
              await loadLatestDataForFeature4(areaType, processedData.bestPotential, processedData.tpByMetro, processedData.tpByRegionId, setFeature4Data, setFeature4Loading, setFeature4Error);
              
              console.log(`[FEATURE4_LOADER] Feature 4 data loaded successfully`);
            } catch (error) {
              console.error(`[FEATURE4_LOADER] Error processing data:`, error);
              setFeature4Error(`Data processing error: ${error.message}`);
              setFeature4Loading(false);
            }
          },
          error: (error) => {
            console.error(`[FEATURE4_LOADER] Parse error:`, error);
            setFeature4Error(`Parse error: ${error.message}`);
            setFeature4Loading(false);
          }
        });

      } catch (error) {
        console.error(`[FEATURE4_LOADER] Load error:`, error);
        setFeature4Error(`Load error: ${error.message}`);
        setFeature4Loading(false);
      }
    };

    // Expose a manual trigger for debugging (call window.__tpDebugLoad() from console)
    if (typeof window !== 'undefined') {
      window.__tpDebugLoad = () => {
        console.error('[FEATURE4_LOADER] ⏩ Manual trigger: loadFeature4Data()');
        return loadFeature4Data();
      };
      // Also allow query param ?forceTpLoad=1 to auto-trigger
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.get('forceTpLoad') === '1') {
          console.error('[FEATURE4_LOADER] ⏩ forceTpLoad=1 detected, invoking loadFeature4Data()');
          loadFeature4Data();
        }
      } catch {}
    }

    loadFeature4Data();

    return () => {
      console.log(`[FEATURE4_LOADER] Cleanup for hook: ${hookId.current}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaType, shouldLoad]);

  // Load RegionID mapping from ranking files (TP_Ready) for Feature 4
  // MUST BE BEFORE chart data useEffect to ensure mapping is loaded first
  useEffect(() => {
    console.error(`[FEATURE4_LOADER] ===== REGIONID MAPPING USEEFFECT START - VERSION 2.5 ===== (ERROR LEVEL LOG TO MAKE IT VISIBLE)`);
    console.error(`[FEATURE4_LOADER] RegionID mapping useEffect triggered for areaType: "${areaType}", shouldLoad: ${shouldLoad}`);
    console.error(`[FEATURE4_LOADER] RegionID mapping useEffect - areaType type: ${typeof areaType}, value: ${JSON.stringify(areaType)}`);
    
    if (!areaType) {
      console.error(`[FEATURE4_LOADER] RegionID mapping useEffect: areaType is undefined/null/empty, skipping`);
      mappingLoadingRef.current = false;
      return;
    }
    
    // If shouldLoad is false, don't load RegionID mapping yet
    if (!shouldLoad) {
      console.error(`[FEATURE4_LOADER] RegionID mapping useEffect: shouldLoad is false, skipping`);
      mappingLoadingRef.current = false;
      return;
    }
    
    console.log(`[FEATURE4_LOADER] RegionID mapping useEffect: areaType is valid, proceeding`);
    
    // IMPORTANT: Use lowercase for cache key to match how it's accessed in processChartData
    const levelDir = areaType.toLowerCase();
    const cacheKey = `${levelDir}_mapping`;
    console.log(`[FEATURE4_LOADER] RegionID mapping cacheKey: ${cacheKey} (normalized to lowercase)`);
    console.log(`[FEATURE4_LOADER] RegionID mapping cache has key: ${regionIdMapping.has(cacheKey)}`);
    
    if (regionIdMapping.has(cacheKey)) {
      console.error(`[FEATURE4_LOADER] RegionID mapping already loaded for ${areaType}: ${regionIdMapping.get(cacheKey).size} entries`);
      mappingLoadingRef.current = false;
      return;
    }

    console.log(`[FEATURE4_LOADER] RegionID mapping not found in cache, will load from file`);
    mappingLoadingRef.current = true; // Mark as loading
    
    try {
      console.log(`[FEATURE4_LOADER] Loading RegionID mapping for ${areaType} from ranking files (TP_Ready) with R2-first strategy`);
      const r2Key = `public/data/Markets_TP/TP_Ready/${levelDir}_markets_TP_turning_points_ready.csv`;
      const rankingUrl = `/data/Markets_TP/TP_Ready/${levelDir}_markets_TP_turning_points_ready.csv?v=${Date.now()}`;
      console.error(`[FEATURE4_LOADER] Fetching RegionID mapping. R2 key: ${r2Key} | fallback: ${rankingUrl}`);
      
      (async () => {
        let text = await fetchR2CsvText(r2Key);
        console.log(`[FEATURE4_LOADER] RegionID mapping loaded from R2`);

        console.log(`[FEATURE4_LOADER] RegionID mapping text loaded, length: ${text.length}`);
        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: 'greedy',
          complete: (result) => {
            console.log(`[FEATURE4_LOADER] RegionID mapping CSV parsed, rows: ${result.data.length}`);
            const mapping = new Map();
            const nameKey = getNameKeyByAreaType(areaType);
            console.log(`[FEATURE4_LOADER] Using nameKey: ${nameKey} for RegionID mapping`);
            
            let mappedCount = 0;
            let skippedCount = 0;
            result.data.forEach(row => {
              if (row[nameKey] && row.RegionID) {
                // Handle multiple RegionIDs per name by storing arrays
                if (mapping.has(row[nameKey])) {
                  const existing = mapping.get(row[nameKey]);
                  if (Array.isArray(existing)) {
                    existing.push(row.RegionID);
                    console.error(`[FEATURE4_LOADER] ⚠️ MULTIPLE RegionIDs for "${row[nameKey]}": [${existing.join(', ')}]`);
                  } else {
                    mapping.set(row[nameKey], [existing, row.RegionID]);
                    console.error(`[FEATURE4_LOADER] ⚠️ MULTIPLE RegionIDs for "${row[nameKey]}": [${existing}, ${row.RegionID}]`);
                  }
                } else {
                  mapping.set(row[nameKey], row.RegionID);
                }
                mappedCount++;
                if (mappedCount <= 5) {
                  console.log(`[FEATURE4_LOADER] Mapped ${row[nameKey]} -> RegionID ${row.RegionID}`);
                }
              } else {
                skippedCount++;
                if (skippedCount <= 3) {
                  console.error(`[FEATURE4_LOADER] Skipping row - missing nameKey "${nameKey}" or RegionID. Row keys:`, Object.keys(row));
                }
              }
            });
            console.error(`[FEATURE4_LOADER] RegionID mapping summary - mapped: ${mappedCount}, skipped: ${skippedCount}, total rows: ${result.data.length}`);
            
            // Debug: Check for duplicate names with different RegionIDs
            const duplicateNames = Array.from(mapping.entries()).filter(([name, rid]) => Array.isArray(rid));
            if (duplicateNames.length > 0) {
              console.error(`[FEATURE4_LOADER] ⚠️ FOUND ${duplicateNames.length} names with multiple RegionIDs:`, duplicateNames.slice(0, 10));
            }
            
            regionIdMapping.set(cacheKey, mapping);
            mappingLoadingRef.current = false; // Mark as loaded
            console.error(`[FEATURE4_LOADER] ✅ RegionID mapping loaded for ${areaType} from ranking files: ${mapping.size} entries`);
            console.error(`[FEATURE4_LOADER] Sample mappings:`, Array.from(mapping.entries()).slice(0, 3));
          },
          error: (error) => {
            mappingLoadingRef.current = false;
            console.error(`[FEATURE4_LOADER] RegionID mapping parse error:`, error);
          }
        });
      })().catch(error => {
        mappingLoadingRef.current = false;
        console.error(`[FEATURE4_LOADER] RegionID mapping fetch error:`, error);
      });
    } catch (error) {
      mappingLoadingRef.current = false;
      console.error(`[FEATURE4_LOADER] Error loading RegionID mapping for ${areaType}:`, error);
    }
  }, [areaType, shouldLoad]);

  // Load chart data automatically when selectedName changes (like Feature 1)
  useEffect(() => {
    console.log(`[FEATURE4_LOADER] ===== CHART DATA USEEFFECT START =====`);
    console.log(`[FEATURE4_LOADER] useEffect triggered for automatic chart loading:`, {
      shouldLoad,
      areaType,
      selectedName,
      hasSelectedName: !!selectedName,
      selectedNameLength: selectedName?.length || 0,
      hasLoadedChart: hasLoadedChartRef.current
    });
    
    if (!shouldLoad) {
      console.log(`[FEATURE4_LOADER] Early return: shouldLoad is false`);
      // Clear chart data when shouldLoad becomes false
      setFeature4Data(prev => ({ ...prev, chartData: [] }));
      return;
    }
    if (!areaType) {
      console.log(`[FEATURE4_LOADER] Early return: areaType is empty`);
      return;
    }
    if (!selectedName) {
      console.log(`[FEATURE4_LOADER] Early return: selectedName is empty`);
      // Clear chart data when no selection
      setFeature4Data(prev => ({ ...prev, chartData: [] }));
      return;
    }

    // Reset chart loading flag for new selections
    const wasAlreadyLoaded = hasLoadedChartRef.current;
    hasLoadedChartRef.current = false;

    // Clear chart data immediately to prevent showing stale/wrong data (only if not already loaded for this selection)
    if (!wasAlreadyLoaded) {
      console.log(`[FEATURE4_LOADER] Clearing old chart data to prevent showing wrong data`);
      setFeature4Data(prev => ({ ...prev, chartData: [] }));
    } else {
      console.log(`[FEATURE4_LOADER] Chart already loaded for this selection, keeping existing data during RegionID reload`);
    }

    console.log(`[FEATURE4_LOADER] All conditions met, proceeding with chart data loading`);

    const loadChartDataAuto = async () => {
      try {
        const levelDir = areaType.toLowerCase();
        const cacheKey = `${levelDir}_mapping`;
        
        // Fast render strategy: Load with name matching first, then reload with RegionID if available
        const loadChartWithBestMethod = () => {
          // Check if mapping is ready or being loaded
          const mappingReady = regionIdMapping.has(cacheKey);
          const mappingLoading = mappingLoadingRef.current;
          
          console.error(`[FEATURE4_LOADER] Chart loading check - mapping ready: ${mappingReady}, mapping loading: ${mappingLoading}, cacheKey: "${cacheKey}"`);
          console.error(`[FEATURE4_LOADER] Available cache keys:`, Array.from(regionIdMapping.keys()));
          
          // If mapping is ready, use it immediately
          if (mappingReady) {
            console.error(`[FEATURE4_LOADER] ✅ RegionID mapping is ready, loading chart data with RegionID matching`);
            loadChartData();
            return;
          }
          
          // If mapping is currently loading, wait for it (up to 5 seconds)
          if (mappingLoading) {
            console.error(`[FEATURE4_LOADER] ⏳ RegionID mapping is loading, waiting for it to complete...`);
            let waitAttempts = 0;
            const waitForMapping = () => {
              if (regionIdMapping.has(cacheKey)) {
                console.error(`[FEATURE4_LOADER] ✅ RegionID mapping now ready after wait, loading chart with RegionID matching`);
                loadChartData();
                return;
              }
              if (mappingLoadingRef.current && waitAttempts < 50) { // Wait up to 5 seconds
                waitAttempts++;
                setTimeout(waitForMapping, 100);
              } else {
                // Mapping didn't load in time, fall back to name matching
                console.error(`[FEATURE4_LOADER] ⚠️ RegionID mapping did not load in time, using name matching`);
                loadChartData();
              }
            };
            waitForMapping();
            return;
          }
          
          // Otherwise, load with name matching immediately for fast render
          console.error(`[FEATURE4_LOADER] ⚡ Loading chart with name matching (fast render), will reload with RegionID if mapping becomes available`);
          loadChartData();
          
          // Then check periodically if mapping becomes available, and reload if it does
          let checkAttempts = 0;
          let hasReloadedWithRegionId = false;
          const checkForMapping = () => {
            if (hasReloadedWithRegionId) return; // Already reloaded, stop checking
            
            if (regionIdMapping.has(cacheKey)) {
              if (checkAttempts < 50) { // Only reload if within time window
                console.error(`[FEATURE4_LOADER] ✅ RegionID mapping now available, reloading chart with RegionID matching`);
                hasReloadedWithRegionId = true;
                loadChartData(); // Reload with proper RegionID matching
              }
              return;
            }
            if (mappingLoadingRef.current && checkAttempts < 100) { // Increase timeout window
              checkAttempts++;
              setTimeout(checkForMapping, 100);
            }
          };
          checkForMapping();
        };

        const loadChartData = async () => {
          // Use Markets_TP Long Format files for automatic chart loading (R2-first)
          const chartUrl = `/data/Markets_TP/Long Format/${levelDir}_markets_TP_long.csv?v=${Date.now()}`;
          console.log(`[FEATURE4_LOADER] Automatic loading chart data for ${selectedName} (R2-first) from: ${chartUrl}`);
          
          let text = null;
          const r2Key = `public/data/Markets_TP/Long Format/${levelDir}_markets_TP_long.csv`; 
          console.log(`[FEATURE4_LOADER] Attempt R2 Long Format key: ${r2Key}`);
          text = await fetchR2CsvText(r2Key);
          console.log(`[FEATURE4_LOADER] CSV data loaded, length: ${text.length} characters`);
          
          Papa.parse(text, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: 'greedy',
            complete: (result) => {
              console.log(`[FEATURE4_LOADER] CSV parsed, rows: ${result.data.length}`);
              const chartData = processChartData(result.data, selectedName, areaType);
              console.error(`[FEATURE4_LOADER] ✅ Chart data processed: ${chartData.length} points`);
              console.log(`[FEATURE4_LOADER] Sample chart data:`, chartData.slice(0, 3));
              setFeature4Data(prev => ({ ...prev, chartData }));
              console.error(`[FEATURE4_LOADER] ✅ Chart data loaded for ${selectedName}: ${chartData.length} points`);
              hasLoadedChartRef.current = true;
            }
          });
        };

        loadChartWithBestMethod();

      } catch (error) {
        console.error(`[FEATURE4_LOADER] Chart data error:`, error);
      }
    };

    loadChartDataAuto();

  }, [areaType, selectedName, shouldLoad, setFeature4Data]);

  // Chart data loader for Feature 4 - Supports Price (Markets_TP Long Format) and Rent/Inventory/Days (Full merged data)
  const loadChartData = async (selectedName, metric = 'Price', regionId = null) => {
    if (!areaType || !selectedName) {
      console.log(`[FEATURE4_LOADER] loadChartData - early return: areaType="${areaType}", selectedName="${selectedName}"`);
      return;
    }

    try {
      const levelDir = areaType.toLowerCase();
      let chartUrl;
      
      // For Price: use Markets_TP Long Format files (only has Price data)
      // For Rent/Inventory/Days: use full merged data files
      if (metric === 'Price') {
        chartUrl = `/data/Markets_TP/Long Format/${levelDir}_markets_TP_long.csv?v=${Date.now()}`;
      } else {
        chartUrl = `/data/Merged_Full_Data/${levelDir}_5y_50k_merged.csv?v=${Date.now()}`;
      }
      
      console.log(`[FEATURE4_LOADER] Loading chart data for ${selectedName}, metric: ${metric} from: ${chartUrl}`);

      const r2KeyChart = metric === 'Price'
        ? `public/data/Markets_TP/Long Format/${levelDir}_markets_TP_long.csv`
        : `public/data/Merged_Full_Data/${levelDir}_5y_50k_merged.csv`;
      console.log(`[FEATURE4_LOADER] Attempt R2 chart key: ${r2KeyChart}`);
      const text = await fetchR2CsvText(r2KeyChart);
      console.log(`[FEATURE4_LOADER] Chart data text length: ${text.length}`);
      
      Papa.parse(text, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: 'greedy',
        complete: (result) => {
            console.log(`[FEATURE4_LOADER] Papa.parse complete - parsed ${result.data.length} rows`);
            const chartData = processChartData(result.data, selectedName, areaType, metric, regionId);
           
           // CRITICAL FIX: Re-select TP with floor rule validation when chart data is available
           // Only validate floor rule for Price charts (TP is based on Price data)
           setFeature4Data(prev => {
             const updated = { ...prev, chartData };
             
             // Floor rule validation only applies to Price charts
             if (metric !== 'Price') {
               console.log(`[FEATURE4_LOADER] Skipping floor rule validation for ${metric} chart`);
               return updated;
             }
             
             // Get RegionID from chart data (matches selected area)
             const chartRegionId = chartData.length > 0 ? chartData[0]?.regionId : null;
             
             if (!chartRegionId || !prev.tpByRegionId || !prev.tpByRegionId[chartRegionId]) {
               return updated;
             }
             
             // For now, validate the currently selected TP
             const currentTpData = prev.tpByRegionId[chartRegionId];
             
             // Validate floor rule (like Feature 3: floor_entire_tail=true, floor_tolerance=0.0)
             const passesFloor = passesFloorRule(chartData, currentTpData.tp_date, 0.0, true, 0);
             
             if (!passesFloor) {
               console.error(`[FEATURE4_LOADER] ❌ FLOOR RULE FAILED for RegionID ${chartRegionId}, TP ${currentTpData.tp_date}`);
               console.error(`[FEATURE4_LOADER] This TP should NOT be displayed. Removing from all collections.`);
               
               // Remove TP that fails floor rule
               const updatedTpByRegionId = { ...prev.tpByRegionId };
               delete updatedTpByRegionId[chartRegionId];
               
               // Also remove from bestPotential
               const updatedBestPotential = prev.bestPotential.filter(
                 item => item.regionId !== chartRegionId
               );
               
               // Also remove from tpByMetro (backward compatibility)
               const nameKey = getNameKeyByAreaType(areaType);
               const selectedNameKey = selectedName;
               const updatedTpByMetro = { ...prev.tpByMetro };
               if (updatedTpByMetro[selectedNameKey]) {
                 delete updatedTpByMetro[selectedNameKey];
               }
               
               console.error(`[FEATURE4_LOADER] TP removed successfully. No TP will be displayed for this RegionID.`);
               
               return {
                 ...updated,
                 tpByRegionId: updatedTpByRegionId,
                 bestPotential: updatedBestPotential,
                 tpByMetro: updatedTpByMetro
               };
             } else {
               console.error(`[FEATURE4_LOADER] ✅ FLOOR RULE PASSED for RegionID ${chartRegionId}, TP ${currentTpData.tp_date}`);
             }
             
             return updated;
           });
           
           console.log(`[FEATURE4_LOADER] Chart data loaded for ${selectedName} - ${chartData.length} points`);
        },
        error: (error) => {
          console.error(`[FEATURE4_LOADER] Papa.parse error:`, error);
        }
      });

    } catch (error) {
      console.error(`[FEATURE4_LOADER] Chart data error:`, error);
    }
  };

  // Return Feature 4 specific utilities
  return {
    loadChartData
  };
}

/**
 * Process ONLY latest data (FAST - no long format loading)
 */
function processLatestDataOnly(latestRows, areaType, bestPotential, tpByMetro, tpByRegionId) {
  const nameKey = getNameKeyForSnapshotData(areaType);
  console.log(`[FEATURE4_LOADER] 🚀 processLatestDataOnly - FAST MODE (no chart data)`);
  
  // Build latestByMetro and latestByRegionId from latest snapshot
  const latestByMetro = {};
  const latestByRegionId = {};
  
  latestRows.forEach(row => {
    const name = row[nameKey] || row.Metro || row.State || row.CountyName || row.County || row.City || row.Zipcode || row.Neighborhood;
    const regionId = row.RegionID || row.regionId || row.region_id;
    
    // Mirror the normalization logic used in processLatestData / processLatestDataWithFullRange
    // so snapshot tables (MetroDataTable) receive the fields they expect:
    // PriceDelta, PriceYoY, DaysDelta, InventoryDelta, RentDelta, Inventory, Days, etc.
    if (name && !latestByMetro[name]) {
      latestByMetro[name] = {
        metro: name,
        regionId: regionId,
        ...row,
        // Normalize field names expected by UI components (like Feature 1, Feature 2, Feature 4 snapshots)
        PriceDelta: row.Price_delta,
        PriceYoY: row.Price_YoY_delta || row.PriceYoY, // Map raw CSV to normalized name
        PriceYoYDelta: row.Price_YoY_delta,
        RentDelta: row.Rent_delta,
        InventoryDelta: row.Inventory_delta,
        DaysDelta: row.Days_delta ?? row.DOM_delta, // Handle DOM vs Days naming
        // Ensure Inventory and Days fields are available (handle case variations)
        Inventory: Number.isFinite(row.Inventory) ? row.Inventory : (Number.isFinite(row.inventory) ? row.inventory : null),
        // Days: Check Days first, then DOM, then lowercase variations - prioritize valid numbers
        Days: Number.isFinite(row.Days) ? row.Days :
              (Number.isFinite(row.DOM) ? row.DOM :
               (Number.isFinite(row.days) ? row.days :
                (Number.isFinite(row.dom) ? row.dom : null)))
      };
    }
    if (regionId && !latestByRegionId[regionId]) {
      latestByRegionId[regionId] = {
        metro: name,
        regionId: regionId,
        ...row,
        // Same normalization for RegionID index
        PriceDelta: row.Price_delta,
        PriceYoY: row.Price_YoY_delta || row.PriceYoY,
        PriceYoYDelta: row.Price_YoY_delta,
        RentDelta: row.Rent_delta,
        InventoryDelta: row.Inventory_delta,
        DaysDelta: row.Days_delta ?? row.DOM_delta,
        Inventory: Number.isFinite(row.Inventory) ? row.Inventory : (Number.isFinite(row.inventory) ? row.inventory : null),
        Days: Number.isFinite(row.Days) ? row.Days :
              (Number.isFinite(row.DOM) ? row.DOM :
               (Number.isFinite(row.days) ? row.days :
                (Number.isFinite(row.dom) ? row.dom : null)))
      };
    }
  });
  
  console.log(`[FEATURE4_LOADER] ✅ Built latestByMetro: ${Object.keys(latestByMetro).length} entries`);
  console.log(`[FEATURE4_LOADER] ✅ Built latestByRegionId: ${Object.keys(latestByRegionId).length} entries`);
  
  // Match best potential with latest data
  const matchedBestPotential = bestPotential.map(bp => {
    const latestData = latestByRegionId[bp.RegionID] || latestByMetro[bp.Metro] || {};
    return { ...bp, ...latestData };
  });
  
  // Get latest date from snapshot
  const latestDate = latestRows.length > 0 ? latestRows[0].Date : '';
  
  console.log(`[FEATURE4_LOADER] ⚡ FAST processing complete - Latest date: ${latestDate}`);
  
  return {
    allDates: [latestDate], // Only latest date for now (full dates loaded on chart request)
    latestByMetro,
    latestByRegionId,
    bestPotential: matchedBestPotential,
    tpByMetro,
    tpByRegionId: tpByRegionId || {},
    latestDate,
    chartData: [] // Empty - will load on demand when user clicks chart buttons
  };
}

/**
 * Process latest data with full date range from Markets_TP Long Format
 */
function processLatestDataWithFullRange(latestRows, longFormatRows, areaType, bestPotential, tpByMetro, tpByRegionId) {
  const nameKey = getNameKeyForSnapshotData(areaType);  // Use snapshot data name key
  const allDates = [];
  const latestByMetro = {};  // Will be keyed by name for backward compatibility
  const latestByRegionId = {};  // NEW: Index by RegionID for exact matching
  let latestDate = "";

  // Process latest snapshot data for Rent/Inventory/DOM
  console.log(`[FEATURE4_LOADER] processLatestDataWithFullRange - Processing ${latestRows.length} latest rows for ${areaType}`);
  console.log(`[FEATURE4_LOADER] processLatestDataWithFullRange - nameKey: ${nameKey}`);
  console.log(`[FEATURE4_LOADER] processLatestDataWithFullRange - Sample latest row:`, latestRows[0]);
  
  latestRows.forEach(row => {
    const name = row[nameKey] || row.Metro || row.State || row.CountyName || row.County || row.City || row.Zipcode || row.Neighborhood;
    if (!name) {
      console.log(`[FEATURE4_LOADER] Skipping latest row - no name found. Keys:`, Object.keys(row));
      return;
    }

    const regionId = row.RegionID || row.regionid || row.region_id;
    
    // Get date from LatestDate or similar column
    const date = row.LatestDate || row.latest_date || row.Date || row.date;
    if (date && !allDates.includes(date)) {
      allDates.push(date);
      if (!latestDate || date > latestDate) {
        latestDate = date;
      }
    }

    // Index by name for backward compatibility - with field normalization
    if (!latestByMetro[name]) {
      latestByMetro[name] = {
        metro: name,
        regionId: regionId,
        ...row,
        // Normalize field names expected by UI components (like Feature 1 and Feature 2)
        PriceDelta: row.Price_delta,
        PriceYoY: row.Price_YoY_delta || row.PriceYoY, // Map CSV field to normalized name
        PriceYoYDelta: row.Price_YoY_delta,
        RentDelta: row.Rent_delta,
        InventoryDelta: row.Inventory_delta,
        DaysDelta: row.Days_delta ?? row.DOM_delta, // Handle DOM vs Days naming
        // Ensure Inventory and Days fields are available (handle case variations)
        Inventory: Number.isFinite(row.Inventory) ? row.Inventory : (Number.isFinite(row.inventory) ? row.inventory : null),
        // Days: Check Days first, then DOM, then lowercase variations - prioritize valid numbers
        Days: Number.isFinite(row.Days) ? row.Days : 
              (Number.isFinite(row.DOM) ? row.DOM : 
               (Number.isFinite(row.days) ? row.days : 
                (Number.isFinite(row.dom) ? row.dom : null)))
      };
    }

    // NEW: Also index by RegionID for exact matching - with field normalization
    if (regionId && !latestByRegionId[regionId]) {
      latestByRegionId[regionId] = {
        metro: name,
        regionId: regionId,
        ...row,
        // Normalize field names expected by UI components (like Feature 1 and Feature 2)
        PriceDelta: row.Price_delta,
        PriceYoY: row.Price_YoY_delta || row.PriceYoY, // Map CSV field to normalized name
        PriceYoYDelta: row.Price_YoY_delta,
        RentDelta: row.Rent_delta,
        InventoryDelta: row.Inventory_delta,
        DaysDelta: row.Days_delta ?? row.DOM_delta, // Handle DOM vs Days naming
        // Ensure Inventory and Days fields are available (handle case variations)
        Inventory: Number.isFinite(row.Inventory) ? row.Inventory : (Number.isFinite(row.inventory) ? row.inventory : null),
        // Days: Check Days first, then DOM, then lowercase variations - prioritize valid numbers
        Days: Number.isFinite(row.Days) ? row.Days : 
              (Number.isFinite(row.DOM) ? row.DOM : 
               (Number.isFinite(row.days) ? row.days : 
                (Number.isFinite(row.dom) ? row.dom : null)))
      };
    }
  });

  // Process long format data for full date range
  const allDatesFromLongFormat = [];
  longFormatRows.forEach(row => {
    const date = row.Date;
    if (date && !allDatesFromLongFormat.includes(date)) {
      allDatesFromLongFormat.push(date);
    }
  });

  // Use the full date range from long format data
  const fullDateRange = allDatesFromLongFormat.sort();
  const actualLatestDate = fullDateRange.length > 0 ? fullDateRange[fullDateRange.length - 1] : latestDate;
  
  console.log(`[FEATURE4_LOADER] Date range analysis:`);
  console.log(`  - Long format rows: ${longFormatRows.length}`);
  console.log(`  - Unique dates from long format: ${allDatesFromLongFormat.length}`);
  console.log(`  - Date range: ${fullDateRange[0]} to ${fullDateRange[fullDateRange.length - 1]}`);
  console.log(`  - Latest date: ${actualLatestDate}`);

  // Match bestPotential to latestByRegionId using RegionID
  const matchedBestPotential = bestPotential.map(item => {
    // Find matching latest data by RegionID
    const latestData = item.regionId && latestByRegionId[item.regionId] 
      ? latestByRegionId[item.regionId]
      : null;
    
    return {
      ...item,
      latestData: latestData,
      // Add PriceYoY to bestPotential item so BestPotentialTable can find it
      PriceYoY: latestData?.PriceYoY ?? latestData?.Price_YoY_delta ?? null,
    };
  });

  // Generate chart data for the first metro in bestPotential (for initial display)
  let initialChartData = [];
  if (matchedBestPotential.length > 0) {
    const firstMetro = matchedBestPotential[0].metro;
    console.log(`[FEATURE4_LOADER] Generating initial chart data for: ${firstMetro}`);
    initialChartData = longFormatRows
      .filter(row => {
        const nameKey = getNameKeyByAreaType(areaType);
        const rowName = row[nameKey];
        return rowName === firstMetro;
      })
      .map(row => ({
        date: row.Date,
        price: row.Price,
        rent: null,
        inventory: null,
        days: null
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    console.log(`[FEATURE4_LOADER] Initial chart data generated: ${initialChartData.length} points`);
  }

  console.log(`[FEATURE4_LOADER] processLatestDataWithFullRange completed:`);
  console.log(`  - latestByMetro keys: ${Object.keys(latestByMetro).length}`);
  console.log(`  - latestByRegionId keys: ${Object.keys(latestByRegionId).length}`);
  console.log(`  - bestPotential items: ${matchedBestPotential.length}`);
  console.log(`  - Sample latestByMetro keys:`, Object.keys(latestByMetro).slice(0, 5));

  return {
    allDates: fullDateRange,  // Use full date range from long format
    latestByMetro,
    latestByRegionId,  // NEW: Add RegionID index
    bestPotential: matchedBestPotential,  // Updated with latest data
    tpByMetro,  // Keep for backward compatibility
    tpByRegionId: tpByRegionId || {},  // NEW: Primary lookup by RegionID - NO NAME MATCHING
    latestDate: actualLatestDate,  // Use latest date from long format
    chartData: initialChartData  // Initial chart data for first metro
  };
}

/**
 * Load latest data for Feature 4 snapshots (OPTIMIZED: Long Format loaded on-demand for charts only)
 */
async function loadLatestDataForFeature4(areaType, bestPotential, tpByMetro, tpByRegionId, setFeature4Data, setFeature4Loading, setFeature4Error) {
  try {
    const levelDir = areaType.toLowerCase();
    
    // OPTIMIZATION: Only load latest snapshot initially (Long Format deferred to chart loading)
    const latestUrl = `/data/Merged_Last_Month_Data/${levelDir}_5y_50k_merged.csv?v=${Date.now()}`;
    
    console.log(`[FEATURE4_LOADER] 🚀 FAST LOAD: Loading ONLY latest snapshot (Long Format deferred)`);
    console.log(`[FEATURE4_LOADER] Loading latest data from: ${latestUrl}`);
    
    // Load only latest snapshot (R2-first)
    const latestText = await (async () => {
      const r2Key = `public/data/Merged_Last_Month_Data/${levelDir}_5y_50k_merged.csv`;
      console.log(`[FEATURE4_LOADER] Attempt R2 latest key: ${r2Key}`);
      return await fetchR2CsvText(r2Key);
    })();
    
    // Parse latest data for snapshots
    Papa.parse(latestText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: 'greedy',
      complete: (latestResult) => {
        try {
          const latestRows = latestResult.data || [];
          console.log(`[FEATURE4_LOADER] ✅ Parsed ${latestRows.length} latest rows (FAST)`);

          // OPTIMIZATION: Process ONLY latest data (no long format yet)
          const processedData = processLatestDataOnly(latestRows, areaType, bestPotential, tpByMetro, tpByRegionId);

          // Reliability: mask metrics not actually available for the snapshot date
          (async () => {
            try {
              const { maskLatestByAvailability } = await import("../../utils/reliability");
              const maskedLatestByMetro = await maskLatestByAvailability(areaType, processedData.latestByMetro);
              const masked = { ...processedData, latestByMetro: maskedLatestByMetro };
              setFeature4Data(masked);
              setFeature4Loading(false);
              console.log(`[FEATURE4_LOADER] ⚡ FAST LOAD COMPLETE - Table ready! (Charts load on-demand)`);
            } catch (e) {
              console.warn(`[FEATURE4_LOADER] Reliability masking failed, using unmasked data:`, e);
              setFeature4Data(processedData);
              setFeature4Loading(false);
              console.log(`[FEATURE4_LOADER] ⚡ FAST LOAD COMPLETE - Table ready! (Charts load on-demand)`);
            }
          })();
          
        } catch (error) {
          console.error(`[FEATURE4_LOADER] Error processing latest data:`, error);
          setFeature4Error(`Latest data processing error: ${error.message}`);
          setFeature4Loading(false);
        }
      }
    });

  } catch (error) {
    console.error(`[FEATURE4_LOADER] Data load error:`, error);
    setFeature4Error(`Data load error: ${error.message}`);
    setFeature4Loading(false);
  }
}

/**
 * Process Markets TP data for Feature 4
 */
function processMarketsTPData(rows, areaType) {
  const nameKey = getNameKeyByAreaType(areaType);
  console.log(`[FEATURE4_LOADER] processMarketsTPData - areaType: ${areaType}, nameKey: ${nameKey}`);
  
  // CRITICAL FIX: Group by RegionID and select LATEST TP per RegionID (like Feature 3)
  const tpDataByRegionId = {};
  
  // First pass: collect all TP data by RegionID
  rows.forEach(row => {
    const name = row[nameKey] || row.metro || row.Metro || row.name || row.Name;
    if (!name) {
      console.log(`[FEATURE4_LOADER] Skipping row - no name found. Keys:`, Object.keys(row));
      return;
    }

    const regionId = row.RegionID;
    const score = row.Best_Potential_Score || row.best_potential_score;
    const tpDate = row.TP_Date || row.tp_date || row.tp_date;
    const tpFound = row.TP_Found || row.tp_found;

    if (score !== undefined && score !== null && score !== '' && regionId) {
      if (!tpDataByRegionId[regionId]) {
        tpDataByRegionId[regionId] = [];
      }
      
      tpDataByRegionId[regionId].push({
        name: name,
        regionId: regionId,
        score: Number(score),
        tp_date: tpDate ? String(tpDate).trim() : '',
        tp_found: tpFound,
        rawTpDate: tpDate // Keep original for sorting
      });
    }
  });

  // Second pass: select LATEST TP per RegionID (like Feature 3 algorithm)
  const bestPotential = [];
  const tpByMetro = {}; // Keep for backward compatibility
  const tpByRegionId = {}; // NEW: Primary lookup by RegionID - NO NAME MATCHING

  Object.keys(tpDataByRegionId).forEach(regionId => {
    const tps = tpDataByRegionId[regionId];
    
    if (tps.length === 1) {
      // Only one TP for this RegionID
      const tp = tps[0];
      bestPotential.push({
        metro: tp.name,
        regionId: tp.regionId,
        score: tp.score,
        tp_date: tp.tp_date,
        tp_found: tp.tp_found
      });
      
      tpByRegionId[regionId] = {
        tp_date: tp.tp_date,
        score: tp.score,
        regionId: tp.regionId,
        tp_found: tp.tp_found
      };
      
      tpByMetro[tp.name] = {
        tp_date: tp.tp_date,
        score: tp.score,
        regionId: tp.regionId,
        tp_found: tp.tp_found
      };
    } else {
      // Multiple TPs for this RegionID - select LATEST (most recent) like Feature 3
      console.error(`[FEATURE4_LOADER] ⚠️ MULTIPLE TPs for RegionID ${regionId}:`, tps.map(tp => ({
        name: tp.name,
        tp_date: tp.tp_date,
        score: tp.score
      })));
      
      // Sort by TP date (ascending) and take the LAST one (most recent)
      const sortedTps = tps.sort((a, b) => {
        const dateA = new Date(a.rawTpDate || a.tp_date);
        const dateB = new Date(b.rawTpDate || b.tp_date);
        return dateA - dateB; // Ascending order
      });
      
      const latestTp = sortedTps[sortedTps.length - 1]; // Take LAST (most recent)
      
      console.error(`[FEATURE4_LOADER] ✅ SELECTED LATEST TP for RegionID ${regionId}:`, {
        selected: {
          name: latestTp.name,
          tp_date: latestTp.tp_date,
          score: latestTp.score
        },
        allTps: sortedTps.map(tp => ({
          name: tp.name,
          tp_date: tp.tp_date,
          score: tp.score
        }))
      });
      
      bestPotential.push({
        metro: latestTp.name,
        regionId: latestTp.regionId,
        score: latestTp.score,
        tp_date: latestTp.tp_date,
        tp_found: latestTp.tp_found
      });
      
      tpByRegionId[regionId] = {
        tp_date: latestTp.tp_date,
        score: latestTp.score,
        regionId: latestTp.regionId,
        tp_found: latestTp.tp_found
      };
      
      tpByMetro[latestTp.name] = {
        tp_date: latestTp.tp_date,
        score: latestTp.score,
        regionId: latestTp.regionId,
        tp_found: latestTp.tp_found
      };
    }
  });

  // Sort by score descending
  bestPotential.sort((a, b) => b.score - a.score);
  
  console.error(`[FEATURE4_LOADER] ✅ LATEST TP ALGORITHM COMPLETED - processed ${bestPotential.length} items for ${areaType}`);
  console.error(`[FEATURE4_LOADER] TP data stored by RegionID: ${Object.keys(tpByRegionId).length} entries`);
  console.error(`[FEATURE4_LOADER] Sample processed data:`, bestPotential.slice(0, 3));

  return { bestPotential, tpByMetro, tpByRegionId };
}

/**
 * Process latest data for Feature 4
 */
function processLatestData(rows, areaType, bestPotential, tpByMetro) {
  const nameKey = getNameKeyForSnapshotData(areaType);  // Use snapshot data name key
  const allDates = [];
  const latestByMetro = {};  // Will be keyed by name for backward compatibility
  const latestByRegionId = {};  // NEW: Index by RegionID for exact matching
  let latestDate = "";

  rows.forEach(row => {
    const name = row[nameKey] || row.Metro || row.State || row.CountyName || row.County || row.City || row.Zipcode || row.Neighborhood;
    if (!name) return;

    const regionId = row.RegionID || row.regionid || row.region_id;
    
    // Get date from LatestDate or similar column
    const date = row.LatestDate || row.latest_date || row.Date || row.date;
    if (date && !allDates.includes(date)) {
      allDates.push(date);
      if (!latestDate || date > latestDate) {
        latestDate = date;
      }
    }

    // Index by name for backward compatibility - with field normalization
    if (!latestByMetro[name]) {
      latestByMetro[name] = {
        metro: name,
        regionId: regionId,
        ...row,
        // Normalize field names expected by UI components (like Feature 1 and Feature 2)
        PriceDelta: row.Price_delta,
        PriceYoY: row.Price_YoY_delta || row.PriceYoY, // Map CSV field to normalized name
        PriceYoYDelta: row.Price_YoY_delta,
        RentDelta: row.Rent_delta,
        InventoryDelta: row.Inventory_delta,
        DaysDelta: row.Days_delta ?? row.DOM_delta, // Handle DOM vs Days naming
        // Ensure Inventory and Days fields are available (handle case variations)
        Inventory: Number.isFinite(row.Inventory) ? row.Inventory : (Number.isFinite(row.inventory) ? row.inventory : null),
        // Days: Check Days first, then DOM, then lowercase variations - prioritize valid numbers
        Days: Number.isFinite(row.Days) ? row.Days : 
              (Number.isFinite(row.DOM) ? row.DOM : 
               (Number.isFinite(row.days) ? row.days : 
                (Number.isFinite(row.dom) ? row.dom : null)))
      };
    }

    // NEW: Also index by RegionID for exact matching - with field normalization
    if (regionId && !latestByRegionId[regionId]) {
      latestByRegionId[regionId] = {
        metro: name,
        regionId: regionId,
        ...row,
        // Normalize field names expected by UI components (like Feature 1 and Feature 2)
        PriceDelta: row.Price_delta,
        PriceYoY: row.Price_YoY_delta || row.PriceYoY, // Map CSV field to normalized name
        PriceYoYDelta: row.Price_YoY_delta,
        RentDelta: row.Rent_delta,
        InventoryDelta: row.Inventory_delta,
        DaysDelta: row.Days_delta ?? row.DOM_delta, // Handle DOM vs Days naming
        // Ensure Inventory and Days fields are available (handle case variations)
        Inventory: Number.isFinite(row.Inventory) ? row.Inventory : (Number.isFinite(row.inventory) ? row.inventory : null),
        // Days: Check Days first, then DOM, then lowercase variations - prioritize valid numbers
        Days: Number.isFinite(row.Days) ? row.Days : 
              (Number.isFinite(row.DOM) ? row.DOM : 
               (Number.isFinite(row.days) ? row.days : 
                (Number.isFinite(row.dom) ? row.dom : null)))
      };
    }
  });

  // Match bestPotential to latestByRegionId using RegionID
  const matchedBestPotential = bestPotential.map(item => {
    // Find matching latest data by RegionID
    const latestData = item.regionId && latestByRegionId[item.regionId] 
      ? latestByRegionId[item.regionId]
      : null;
    
    return {
      ...item,
      latestData: latestData,
      // Add PriceYoY to bestPotential item so BestPotentialTable can find it
      PriceYoY: latestData?.PriceYoY ?? latestData?.Price_YoY_delta ?? null,
    };
  });

  return {
    allDates,
    latestByMetro,
    latestByRegionId,  // NEW: Add RegionID index
    bestPotential: matchedBestPotential,  // Updated with latest data
    tpByMetro,
    latestDate,
    chartData: []
  };
}

/**
 * Process chart data for Feature 4 - Supports Price (Markets_TP Long Format) and Rent/Inventory/Days (Full merged data)
 */
function processChartData(rows, selectedName, areaType, metric = 'Price', regionId = null) {
  const nameKey = getNameKeyByAreaType(areaType);
  
  // Map metric names to CSV column names
  const metricMap = {
    'Price': 'Price',
    'Rent': 'Rent',
    'Inventory': 'Inventory',
    'Days': 'Days',
    'DOM': 'Days'
  };
  
  const fieldName = metricMap[metric] || 'Price';
  
  console.log(`[FEATURE4_LOADER] processChartData - selectedName: "${selectedName}", areaType: "${areaType}", nameKey: "${nameKey}"`);
  console.log(`[FEATURE4_LOADER] processChartData - total rows: ${rows.length}`);
  
  // Debug: Show first few rows to see the data structure
  if (rows.length > 0) {
    console.log(`[FEATURE4_LOADER] processChartData - sample rows:`, rows.slice(0, 3));
  }
  
  let filteredRows = [];
  let targetRegionId = regionId;
  
  // If regionId not provided, try to get it from mapping
  if (!targetRegionId) {
    // For metro, state, county, city, zipcode, and neighborhood areas, try RegionID matching first
    if (areaType.toLowerCase() === 'metro' || areaType.toLowerCase() === 'state' || areaType.toLowerCase() === 'county' || areaType.toLowerCase() === 'city' || areaType.toLowerCase() === 'zipcode' || areaType.toLowerCase() === 'neighborhood') {
      const levelDir = areaType.toLowerCase();
      const cacheKey = `${levelDir}_mapping`;
      const mapping = regionIdMapping.get(cacheKey);
      const regionIdData = mapping ? mapping.get(selectedName) : null;
      
      if (regionIdData) {
        // Handle both single RegionID and multiple RegionIDs
        const regionIds = Array.isArray(regionIdData) ? regionIdData : [regionIdData];
        targetRegionId = regionIds[0]; // Use first one
        console.log(`[FEATURE4_LOADER] processChartData - Found RegionID from mapping: ${targetRegionId}`);
      }
    }
  }
  
  // Use RegionID matching if available, otherwise fall back to name matching
  if (targetRegionId) {
    console.log(`[FEATURE4_LOADER] processChartData - Using RegionID matching: ${targetRegionId}`);
    filteredRows = rows.filter(row => row.RegionID === targetRegionId);
    console.log(`[FEATURE4_LOADER] processChartData - RegionID ${targetRegionId} matches found:`, filteredRows.length);
    
    if (filteredRows.length === 0) {
      console.log(`[FEATURE4_LOADER] processChartData - No RegionID matches, falling back to name matching`);
      filteredRows = rows.filter(row => row[nameKey] === selectedName);
      console.log(`[FEATURE4_LOADER] processChartData - Name matches found:`, filteredRows.length);
    }
  } else {
    // No RegionID available, use name matching
    console.log(`[FEATURE4_LOADER] processChartData - No RegionID available, using name matching`);
    filteredRows = rows.filter(row => row[nameKey] === selectedName);
    console.log(`[FEATURE4_LOADER] processChartData - Name matches found:`, filteredRows.length);
  }
  
  console.log(`[FEATURE4_LOADER] processChartData - filtered rows: ${filteredRows.length}`);
  
  // CRITICAL: Extract and verify the RegionID from the chart data
  const chartDataRegionIds = [...new Set(filteredRows.map(r => r.RegionID).filter(Boolean))];
  if (chartDataRegionIds.length > 0) {
    const primaryChartRegionId = chartDataRegionIds[0];
    console.error(`[FEATURE4_LOADER] ✅ Chart data RegionID: ${primaryChartRegionId} for "${selectedName}"`);
    
    if (chartDataRegionIds.length > 1) {
      console.error(`[FEATURE4_LOADER] ⚠️ WARNING: Chart data has ${chartDataRegionIds.length} different RegionIDs!`, chartDataRegionIds);
    }
  } else {
    console.error(`[FEATURE4_LOADER] ⚠️ WARNING: Chart data has NO RegionID!`);
  }
  
  const chartData = filteredRows.map(row => ({
    date: row.Date,
    // Use the selected metric field value as price (chart component expects 'price' field)
    price: row[fieldName] || null,
    // Also store the original values for reference
    rent: row.Rent || null,
    inventory: row.Inventory || null,
    days: row.Days || row.DOM || null,
    // Include RegionID in chart data for verification
    regionId: row.RegionID
  }))
  .filter(row => row.price !== null && row.price !== undefined && !isNaN(row.price))
  .sort((a, b) => new Date(a.date) - new Date(b.date));
  
  console.error(`[FEATURE4_LOADER] processChartData - final chartData length: ${chartData.length}`);
  if (chartData.length > 0) {
    console.error(`[FEATURE4_LOADER] ✅ Chart data RegionID in result: ${chartData[0]?.regionId}`);
    console.log(`[FEATURE4_LOADER] processChartData - sample chartData:`, chartData.slice(0, 3));
  }
  
  return chartData;
}

/**
 * Floor rule validation (like Feature 3)
 * Validates that prices after TP date don't drop below TP price level
 * 
 * Implementation matches Feature 3's passes_floor() function:
 * - tp_price: Price at TP date
 * - allowed_min: tp_price * (1 - floor_tolerance)
 * - Check all prices after TP date (or N years if floor_years > 0)
 * - Returns true if min(price_after_tp) >= allowed_min
 */
function passesFloorRule(chartData, tpDate, floorTolerance = 0.0, floorEntireTail = true, floorYears = 0) {
  if (!chartData || chartData.length === 0) {
    return false; // No data to validate
  }

  // Parse TP date (can be YYYY-MM or YYYY-MM-DD)
  const tpDateStr = String(tpDate).trim();
  const tpDateParsed = new Date(tpDateStr + (tpDateStr.length === 7 ? '-01' : '')); // Add day if YYYY-MM format
  
  // Find TP price from chart data (find nearest date to TP date)
  let tpPrice = null;
  let tpPriceDate = null;
  let minDiff = Infinity;
  
  for (const point of chartData) {
    if (!point.price) continue; // Skip points without price
    
    const pointDate = new Date(point.date);
    const diff = Math.abs(pointDate - tpDateParsed);
    
    if (diff < minDiff) {
      minDiff = diff;
      tpPrice = point.price;
      tpPriceDate = pointDate;
    }
  }
  
  if (tpPrice === null) {
    console.error(`[FEATURE4_LOADER] ❌ Floor rule: Could not find TP price for date ${tpDateStr}`);
    return false; // Can't validate without TP price
  }
  
  // Calculate allowed minimum (with tolerance) - matches Feature 3: allowed_min = tp_price * (1 - floor_tolerance)
  const allowedMin = tpPrice * (1 - floorTolerance);
  
  // Get prices after TP date
  const postTpPrices = chartData
    .filter(point => {
      if (!point.price) return false;
      const pointDate = new Date(point.date);
      return pointDate >= tpPriceDate; // From TP date inclusive
    })
    .map(point => point.price);
  
  if (postTpPrices.length === 0) {
    console.error(`[FEATURE4_LOADER] ❌ Floor rule: No prices after TP date ${tpDateStr}`);
    return false;
  }
  
  // Limit to floorYears if specified (matches Feature 3 logic)
  let pricesToCheck = postTpPrices;
  if (!floorEntireTail && floorYears > 0) {
    // Assume monthly data: 12 periods per year
    pricesToCheck = postTpPrices.slice(0, floorYears * 12);
  }
  
  // Check if minimum price after TP is >= allowed minimum (matches Feature 3: return float(post.min()) >= allowed_min)
  const minAfterTp = Math.min(...pricesToCheck);
  const passes = minAfterTp >= allowedMin;
  
  console.error(`[FEATURE4_LOADER] Floor rule validation for TP ${tpDateStr}:`, {
    tpPrice: tpPrice.toFixed(2),
    allowedMin: allowedMin.toFixed(2),
    minAfterTp: minAfterTp.toFixed(2),
    passes,
    periodsChecked: pricesToCheck.length,
    totalPeriodsAfterTp: postTpPrices.length,
    floorEntireTail,
    floorYears,
    floorTolerance
  });
  
  return passes;
}

/**
 * Get name key by area type
 */
function getNameKeyByAreaType(areaType) {
  const nameMap = {
    'Metro': 'Metro',
    'State': 'State',
    'County': 'County',  // Markets TP data uses 'County' column
    'City': 'City',
    'Zipcode': 'Zipcode',
    'Neighborhood': 'Neighborhood'
  };
  return nameMap[areaType] || 'Metro';
}

function getNameKeyForSnapshotData(areaType) {
  const nameMap = {
    'Metro': 'Metro',
    'State': 'State',
    'County': 'CountyName',  // Snapshot data uses 'CountyName' column
    'City': 'City',
    'Zipcode': 'Zipcode',
    'Neighborhood': 'Neighborhood'
  };
  return nameMap[areaType] || 'Metro';
}

